// Filename: e1copy2/e1c/src/components/MedicationReconciliationModal.js
// Author: Sunil Jain 
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    CircularProgress, Alert, Grid, Paper, IconButton, TextField, Tooltip,
    useTheme, alpha, Switch, Chip, Divider, LinearProgress, MenuItem, Select, InputLabel, FormControl, Autocomplete
} from '@mui/material';
import { keyframes } from '@mui/system';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PersonIcon from '@mui/icons-material/Person';
import ArticleIcon from '@mui/icons-material/Article';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import DeleteIcon from '@mui/icons-material/Delete';
import authFetch from '../utils/authFetch';
import logger from '../utils/logger';

// --- Helper Components ---
const ExtractedDataViewer = ({ data, isLoading, onEdit, onCancel, onSave, onInputChange, onDelete, editingId, editData, isSaving, isDeleting, initialDataIds, justCompleted }) => {
    const theme = useTheme();
    const groupedData = useMemo(() => {
        if (!data || !Array.isArray(data)) return {};
        const getDate = (item) => {
            const dateStr = item?.data_content?.date_time || item?.data_content?.Date || item?.data_content?.date;
            if (!dateStr) return null;
            const d = new Date(dateStr);
            return isNaN(d) ? null : d;
        };
        const grouped = data.reduce((acc, item) => {
            const key = item.data_type || 'uncategorized';
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
        }, {});
        for (const key in grouped) {
            grouped[key].sort((a, b) => {
                const dateA = getDate(a);
                const dateB = getDate(b);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateA - dateB;
            });
        }
        return grouped;
    }, [data]);

    if (isLoading) { return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={25} /></Box>; }
    if (Object.keys(groupedData).length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography variant="caption" color="text.secondary">No additional clinical data was extracted.</Typography>
            </Paper>
        );
    }

    return (
        <Box>{Object.entries(groupedData).map(([type, items]) => (
            <Paper key={type} variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: alpha(theme.palette.info.main, 0.05), borderColor: 'info.main' }}>
                <Typography variant="subtitle1" fontWeight="bold" color="info.dark" sx={{ textTransform: 'capitalize', mb: 1 }}>{type.replace(/_/g, ' ')}</Typography>
                {items.map((item) => {
                    const isNew = justCompleted && initialDataIds.current.summary && !initialDataIds.current.summary.has(item.id);
                    return (
                        <Paper key={item.id} variant="outlined" sx={{ p: 1.5, mb: 1, borderColor: item.review_status === 'POTENTIAL_DUPLICATE' ? 'warning.main' : 'divider', backgroundColor: item.review_status === 'POTENTIAL_DUPLICATE' ? alpha(theme.palette.warning.main, 0.05) : 'transparent' }}>
                            {editingId === item.id ? (
                                <Box>
                                    {Object.entries(editData).map(([key, value]) => (
                                        <TextField key={key} label={key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())} defaultValue={value} onChange={(e) => onInputChange(key, e.target.value)} fullWidth size="small" sx={{ mb: 1 }}/>
                                    ))}
                                    <Box sx={{ textAlign: 'right', mt: 1 }}>
                                        <Button size="small" onClick={onCancel} sx={{ mr: 1 }}>Cancel</Button>
                                        <Button size="small" variant="contained" onClick={() => onSave(item.id)} disabled={isSaving}>Save</Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box>
                                        {Object.keys(item.data_content).length > 1 ? (
                                            Object.entries(item.data_content).map(([key, value]) => (
                                                <Typography key={key} variant="body2" component="span" sx={{ display: 'block', color: isNew ? 'warning.dark' : 'text.primary' }}>
                                                    <Box component="strong" sx={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}:</Box> {String(value)}
                                                </Typography>
                                            ))
                                        ) : (
                                            <Typography variant="body2" sx={{ color: isNew ? 'warning.dark' : 'text.primary' }}>{String(Object.values(item.data_content)[0] || '')}</Typography>
                                        )}
                                        {item.review_status === 'POTENTIAL_DUPLICATE' && ( <Chip label="Potential Duplicate" size="small" color="warning" variant="outlined" sx={{ mt: 1 }} /> )}
                                    </Box>
                                    <Box sx={{ mt: -1, mr: -1 }}>
                                        <Tooltip title="Edit this item"><IconButton size="small" onClick={() => onEdit(item)}><EditIcon fontSize="inherit" /></IconButton></Tooltip>
                                        <Tooltip title="Delete this item"><IconButton size="small" onClick={() => onDelete(item.id)} disabled={isDeleting}><DeleteIcon fontSize="inherit" color="error" /></IconButton></Tooltip>
                                    </Box>
                                </Box>
                            )}
                        </Paper>
                    )
                })}
            </Paper>
        ))}</Box>
    );
};

const FREQUENCY_OPTIONS = [
    { value: 'once', label: 'Once (One Time)' },
    { value: 'daily', label: 'Daily' },
    { value: 'twice_daily', label: 'Twice Daily' },
    { value: 'three_times_daily', label: 'Three Times Daily' },
    { value: 'four_times_daily', label: 'Four Times Daily' },
    { value: 'as_needed', label: 'As Needed' },
    { value: 'specific_days_of_week', label: 'Specific Days of Week' },
    { value: 'every_x_days', label: 'Every X Days' },
    { value: 'tapering_schedule', label: 'Tapering Schedule' },
];

const FREQUENCY_TO_TIMES_MAP = {
  once: [],
  daily: ['09:00'],
  twice_daily: ['09:00', '21:00'],
  three_times_daily: ['08:00', '14:00', '20:00'],
  four_times_daily: ['08:00', '12:00', '16:00', '20:00'],
};

const MedicationRow = ({ med, isUpdating, onEdit, onSave, onCancelEdit, onUpdateStatus, onInputChange, onFrequencyChange, onToggleReminder, editData, isEditing, justCompleted, initialDataIds, handleEditTaperPhaseChange, handleAddNewEditTaperPhase, handleRemoveEditTaperPhase }) => {
    const blinkAnimation = keyframes`
      0% { background-color: ${alpha('#66bb6a', 0)}; }
      50% { background-color: ${alpha('#66bb6a', 0.25)}; }
      100% { background-color: ${alpha('#66bb6a', 0)}; }
    `;
    const wasAlreadyActive = useMemo(() => justCompleted && med.status === 'active' && initialDataIds.current.meds && initialDataIds.current.meds.has(med.id), [justCompleted, med.status, med.id, initialDataIds]);
    const source = med.item_metadata?.source;
    const sourceChip = (source) => {
        const styleProps = { display: 'inline-flex', alignItems: 'center', gap: 0.5, mr: 1.5, mt: { xs: 1, md: 0 }, p: '4px 8px', borderRadius: '16px', border: '1px solid' };
        if (source === 'PROVIDER_ADDED') return (<Tooltip title="This medication was added manually by a member of your care team."><Typography variant="caption" sx={{ ...styleProps, borderColor: 'primary.main', color: 'primary.main' }}><PersonIcon sx={{ fontSize: '16px' }} /> Manual Entry</Typography></Tooltip>);
        if (source === 'EMR' || source === 'DISCHARGE_SUMMARY') return (<Tooltip title="This medication was automatically extracted from a provided document."><Typography variant="caption" sx={{ ...styleProps, borderColor: 'secondary.main', color: 'secondary.main' }}><ArticleIcon sx={{ fontSize: '16px' }} /> From Document</Typography></Tooltip>);
        if (source === 'PATIENT_ADDED') return (<Tooltip title="This medication was self-reported by the patient."><Typography variant="caption" sx={{ ...styleProps, borderColor: 'warning.main', color: 'warning.dark' }}><PersonIcon sx={{ fontSize: '16px' }} /> Self-Reported</Typography></Tooltip>);
        return null;
    };
    return (
        <Paper 
            variant="outlined" 
            sx={{ 
                p: 2, 
                mb: 1.5, 
                transition: 'all 0.3s', 
                borderWidth: '2px', 
                borderColor: med.status === 'active' ? 'success.main' : (med.status === 'pending_review' ? 'warning.main' : 'divider'), 
                // This line now safely uses the locally-scoped blinkAnimation variable
                animation: wasAlreadyActive ? `${blinkAnimation} 1.5s ease-in-out` : 'none' 
            }}
        >            
            {isEditing ? (
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}><TextField size="small" label="Medication Name" name="item_name" value={editData.item_name || ''} onChange={onInputChange} fullWidth /></Grid>
                    <Grid item xs={12} sm={3}><TextField size="small" label="Dosage" name="dosage" value={editData.dosage || ''} onChange={onInputChange} fullWidth /></Grid>
                    <Grid item xs={12} sm={3}><TextField size="small" label="Route" name="route" value={editData.route || ''} onChange={onInputChange} fullWidth /></Grid>

                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Frequency"
                            name="frequency_type"
                            value={editData.frequency_type || 'once'}
                            onChange={onFrequencyChange}
                        >
                            {FREQUENCY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    
                    {editData.frequency_type === 'tapering_schedule' ? (
                        <Grid item xs={12}>
                            <Box sx={{mt: 1, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1}}>
                                <Typography variant="subtitle2" gutterBottom>Define Tapering Schedule</Typography>
                                {editData.tapering_schedule.map((phase, index) => (
                                    <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                                        <Grid container spacing={1.5} alignItems="center">
                                            <Grid item xs={12} sm={5} container spacing={1.5} alignItems="center">
                                                <Grid item xs={5}><TextField fullWidth size="small" type="number" label={`Start Day`} value={phase.start_day} onChange={e => handleEditTaperPhaseChange(index, 'start_day', parseInt(e.target.value) || 1)} /></Grid>
                                                <Grid item xs={2} sx={{textAlign: 'center'}}>-</Grid>
                                                <Grid item xs={5}><TextField fullWidth size="small" type="number" label={`End Day`} value={phase.end_day} onChange={e => handleEditTaperPhaseChange(index, 'end_day', parseInt(e.target.value) || 1)} /></Grid>
                                            </Grid>
                                            <Grid item xs={12} sm={5}>
                                                <TextField select fullWidth size="small" label="Frequency" value={phase.frequency_type} onChange={e => handleEditTaperPhaseChange(index, 'frequency_type', e.target.value)}>
                                                    {FREQUENCY_OPTIONS.filter(o => !['tapering_schedule', 'as_needed', 'once'].includes(o.value)).map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={2} sx={{textAlign: 'right'}}>
                                                <IconButton size="small" onClick={() => handleRemoveEditTaperPhase(index)} disabled={editData.tapering_schedule.length <= 1}>
                                                    <DeleteIcon fontSize="inherit"/>
                                                </IconButton>
                                            </Grid>
                                            <Grid item xs={12}>
                                                {phase.times_of_day.map((time, timeIndex) => (
                                                    <TextField
                                                        key={timeIndex} size="small" label={`Time ${timeIndex + 1}`} type="time" value={time}
                                                        sx={{ width: '120px', mr: 1 }} InputLabelProps={{ shrink: true }}
                                                        onChange={e => {
                                                            const newTimes = [...phase.times_of_day];
                                                            newTimes[timeIndex] = e.target.value;
                                                            handleEditTaperPhaseChange(index, 'times_of_day', newTimes);
                                                        }}
                                                    />
                                                ))}
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ))}
                                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAddNewEditTaperPhase}>Add Phase</Button>
                            </Box>
                        </Grid>
                    ) : (
                         <Grid item xs={12}>
                            {editData.times_of_day.map((time, index) => (
                                <TextField
                                    key={index} size="small" label={`Time ${index + 1}`} type="time" value={time}
                                    onChange={(e) => {
                                        const newTimes = [...editData.times_of_day];
                                        newTimes[index] = e.target.value;
                                        onInputChange({ target: { name: 'times_of_day', value: newTimes }});
                                    }}
                                    sx={{ width: '150px', mr: 1 }} InputLabelProps={{ shrink: true }}
                                />
                            ))}
                        </Grid>
                    )}

                    <Grid item xs={12}><TextField size="small" label="Instruction Text" name="frequency_instruction" value={editData.frequency_instruction || ''} onChange={onInputChange} fullWidth helperText="e.g., Take with food" /></Grid>
                    <Grid item xs={12} sx={{textAlign: 'right'}}><Button size="small" onClick={onCancelEdit} sx={{mr: 1}}>Cancel</Button><Button size="small" variant="contained" onClick={() => onSave(med.id)} disabled={isUpdating}><SaveIcon sx={{mr: 0.5}}/>Save Changes</Button></Grid>
                </Grid>
            ) : (

                <Grid container spacing={1} alignItems="center" justifyContent="space-between">
                <Grid item xs={12} md={8}>
                    <Typography variant="h6" fontWeight="500">{med.details?.item_name || 'N/A'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                    {(med.details?.item_metadata?.dosage || '')}
                    {' '}{(med.details?.item_metadata?.route) ? `(${med.details?.item_metadata?.route})` : ''} - {' '}
                    {(med.details?.item_metadata?.frequency_instruction || '')}
                    </Typography>
                        {sourceChip(source)}
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title={med.details?.reminders_active ? 'AI Reminders are ON' : 'Turn on AI Reminders'}><Switch 
                            checked={!!med.details?.reminders_active} 
                                onChange={(e) => onToggleReminder(e.target.checked)} 
                                disabled={isUpdating} 
                                color="secondary" 
                            /></Tooltip>
                        <Tooltip title="Edit Details"><IconButton size="small" onClick={() => onEdit(med)} disabled={isUpdating}><EditIcon fontSize="inherit" /></IconButton></Tooltip>
                        {med.status === 'pending_review' ? ( <Tooltip title="Verify Medication"><Button variant="outlined" size="small" onClick={() => onUpdateStatus('active')} disabled={isUpdating}>Verify</Button></Tooltip> ) : ( <Tooltip title="Set back to 'Pending Review'"><IconButton onClick={() => onUpdateStatus('pending_review')} size="small" disabled={isUpdating}><UndoIcon fontSize="inherit" /></IconButton></Tooltip> )}
                        <Tooltip title="Remove/Stop"><IconButton size="small" color="error" onClick={() => onUpdateStatus('stopped')} disabled={isUpdating}><DeleteIcon fontSize="inherit" /></IconButton></Tooltip>

                    </Grid>
                </Grid>
            )}
        </Paper>
    );
};

// --- Main Modal Component ---
export default function MedicationReconciliationModal({ open, onClose, patientUserId, episodeId, patientName, showSnackbar, tenantId }) {
    console.log("Tenant ID Prop passed to Modal:", tenantId); 
    const queryClient = useQueryClient();
    const theme = useTheme();

    // --- State Management ---
    const [isProcessingPdf, setIsProcessingPdf] = useState(false);
    const [processingStatus, setProcessingStatus] = useState({ progress: 0, message: '' });
    const [justCompleted, setJustCompleted] = useState(false);
    const initialDataIds = useRef({ meds: null, summary: null });

const [newMedData, setNewMedData] = useState({
        item_name: '',
        frequency_type: 'daily',
        times_of_day: ['09:00'],
        item_metadata: { dosage: '', route: '', frequency_instruction: '' },
        tapering_schedule: [{ start_day: 1, end_day: 7, frequency_type: 'daily', times_of_day: ['09:00'] }]
    });

    const handleNewMedTaperPhaseChange = (index, field, value) => {
        const newSchedule = [...newMedData.tapering_schedule];
        const phase = { ...newSchedule[index] };
        phase[field] = value;

        if (field === 'frequency_type') {
            const defaultTimes = FREQUENCY_TO_TIMES_MAP[value] || [];
            phase.times_of_day = defaultTimes.length > 0 ? defaultTimes : ['09:00'];
        }
        newSchedule[index] = phase;
        setNewMedData(p => ({ ...p, tapering_schedule: newSchedule }));
    };

    const handleAddNewMedTaperPhase = () => {
        const lastPhase = newMedData.tapering_schedule[newMedData.tapering_schedule.length - 1];
        const newStartDay = lastPhase ? (parseInt(lastPhase.end_day, 10) || 0) + 1 : 1;
        setNewMedData(p => ({
            ...p,
            tapering_schedule: [
                ...p.tapering_schedule,
                { start_day: newStartDay, end_day: newStartDay + 6, frequency_type: 'daily', times_of_day: ['09:00'] }
            ]
        }));
    };

    const handleRemoveNewMedTaperPhase = (index) => {
        if (newMedData.tapering_schedule.length <= 1) return;
        const newSchedule = newMedData.tapering_schedule.filter((_, i) => i !== index);
        setNewMedData(p => ({ ...p, tapering_schedule: newSchedule }));
    };

    const [dischargeSummaryFile, setDischargeSummaryFile] = useState(null);
    const [editingMedId, setEditingMedId] = useState(null);
    const [editingSummaryId, setEditingSummaryId] = useState(null);
    const [editSummaryData, setEditSummaryData] = useState({});
    
    const handleEditTaperPhaseChange = (index, field, value) => {
        const newSchedule = [...editFormData.tapering_schedule];
        const phase = { ...newSchedule[index] };
        phase[field] = value;

        if (field === 'frequency_type') {
            const defaultTimes = FREQUENCY_TO_TIMES_MAP[value] || [];
            phase.times_of_day = defaultTimes.length > 0 ? defaultTimes : ['09:00'];
        }
        newSchedule[index] = phase;
        setEditFormData(p => ({ ...p, tapering_schedule: newSchedule }));
    };

    const handleAddNewEditTaperPhase = () => {
        const lastPhase = editFormData.tapering_schedule[editFormData.tapering_schedule.length - 1];
        const newStartDay = lastPhase ? (parseInt(lastPhase.end_day, 10) || 0) + 1 : 1;
        setEditFormData(p => ({
            ...p,
            tapering_schedule: [
                ...p.tapering_schedule,
                { start_day: newStartDay, end_day: newStartDay + 6, frequency_type: 'daily', times_of_day: ['09:00'] }
            ]
        }));
    };

    const handleRemoveEditTaperPhase = (index) => {
        if (editFormData.tapering_schedule.length <= 1) return;
        const newSchedule = editFormData.tapering_schedule.filter((_, i) => i !== index);
        setEditFormData(p => ({ ...p, tapering_schedule: newSchedule }));
    };

    const [editFormData, setEditFormData] = useState({ times_of_day: [], tapering_schedule: [] });
    const [showSaveProtocolDialog, setShowSaveProtocolDialog] = useState(false);
    const [protocolName, setProtocolName] = useState('');
    const [protocolDescription, setProtocolDescription] = useState('');
    const isEpisodeContext = !!episodeId;
    const medsQueryKey = useMemo(() => ['patientAllMedicationDirectives', { patientUserId }], [patientUserId]);
    const { 
        data: meds = [], 
        isLoading, 
        isError, 
        error 
    } = useQuery({
        queryKey: medsQueryKey, 
        queryFn: () => authFetch(`/patients/${patientUserId}/directives`), 
        enabled: !!open && !!patientUserId,
    });

    const { data: careProtocols = [] } = useQuery({
        queryKey: ['careProtocols', tenantId],
        queryFn: () => authFetch('protocols/'),
        enabled: !!open && !!tenantId,
    });

    useEffect(() => {
        if (careProtocols.length > 0) {
            logger.info('[MedRecModal] Received care protocols from API:', careProtocols);
        }
    }, [careProtocols]);

    const { data: patientMedHistory = [] } = useQuery({
        queryKey: ['patientMedicationHistory', patientUserId],
        queryFn: () => authFetch(`/directives/history/patient/${patientUserId}`),
        enabled: !!open && !!patientUserId,
    });

    const sortedMedOptions = useMemo(() => {
        const options = [];
        const seenNames = new Set();

        if (Array.isArray(careProtocols)) {
            logger.info(`[MedRecModal] Processing ${careProtocols.length} received protocols...`);
            
            careProtocols.forEach(protocol => {
                const medDirective = protocol.module_definition?.medication_directives?.[0];
                
                if (medDirective) {
                    const medName = medDirective.item_name || medDirective.med_name;
                    if (medName && !seenNames.has(medName.toLowerCase())) {
                        options.push({
                            label: medName,
                            type: 'Protocol',
                            data: protocol,
                        });
                        seenNames.add(medName.toLowerCase());
                    }
                }
            });
        }

        if (Array.isArray(patientMedHistory)) {
            patientMedHistory.forEach(med => {
                const medName = med.details?.item_name;
                if (medName && !seenNames.has(medName.toLowerCase())) {
                    options.push({
                        label: medName,
                        type: 'Patient History',
                        data: med,
                    });
                    seenNames.add(medName.toLowerCase());
                }
            });
        }

        return options;
    }, [careProtocols, patientMedHistory]);

    const handleAutocompleteChange = (event, newValue) => {
        if (typeof newValue === 'string') {
            setNewMedData(prev => ({ ...prev, item_name: newValue }));
        } else if (newValue && newValue.data) {
            const { type, data } = newValue;

            if (type === 'Protocol') {
                const definitionToUse = data.module_definition?.medication_directives?.[0];
                if (definitionToUse) {
                    setNewMedData({
                        item_name: definitionToUse.item_name || definitionToUse.med_name || '', // Keep legacy fallback for now
                        frequency_type: 'tapering_schedule', // Assuming protocols are tapers for now
                        times_of_day: [],
                        item_metadata: definitionToUse.item_metadata || {},
                        tapering_schedule: definitionToUse.tapering_schedule || [],
                    });
                }
                
            } else { 
                // This block handles 'Patient History'
                setNewMedData({
                    item_name: data.details?.item_name,
                    frequency_type: data.details?.frequency_type || 'daily',
                    times_of_day: data.details?.times_of_day || ['09:00'],
                    item_metadata: data.details?.item_metadata || {},

                    // --- START OF SURGICAL FIX ---
                    // BUG: This was looking in item_metadata, causing an empty schedule to load.
                    // tapering_schedule: data.details?.item_metadata?.tapering_schedule || [{ start_day: 1, end_day: 7, frequency_type: 'daily', times_of_day: ['09:00'] }],
                    // FIX: Read from the correct location: data.details.tapering_schedule
                    tapering_schedule: data.details?.tapering_schedule || [{ start_day: 1, end_day: 7, frequency_type: 'daily', times_of_day: ['09:00'] }],
                    // --- END OF SURGICAL FIX ---
                });
            }
        } else {
            
            setNewMedData(prev => ({ ...prev, item_name: '' }));
        }
    };

    const handleDeleteProtocol = (protocolId, event) => {
        event.stopPropagation(); 
        if (window.confirm("Are you sure you want to permanently delete this protocol?")) {
            deleteProtocolMutation.mutate(protocolId);
        }
    };

    const { data: allPatientEpisodes = [] } = useQuery({
        queryKey: ['patientEpisodes', patientUserId],
        queryFn: () => authFetch(`episodes/?patient_user_id=${patientUserId}&limit=100`),
        enabled: !!open && !!patientUserId,
    });

    const perpetualEpisodeId = useMemo(() => {
        if (!allPatientEpisodes || allPatientEpisodes.length === 0) return null;
        const perpetualEp = allPatientEpisodes.find(ep => ep.episode_type === 'lifetime_perpetual');
        return perpetualEp?.id || null;
    }, [allPatientEpisodes]);

    const targetEpisodeId = episodeId || perpetualEpisodeId;
    const summaryQueryKey = useMemo(() => ['patientExtractedSummary', { patientUserId, targetEpisodeId }], [patientUserId, targetEpisodeId]);
    const { data: extractedSummaryData, isLoading: isLoadingSummary } = useQuery({
        queryKey: summaryQueryKey,
        queryFn: () => authFetch(`/episodes/extracted-summary/${targetEpisodeId}`),
        enabled: !!open && !!patientUserId && !!targetEpisodeId,
    });

    useEffect(() => {
        if (!isLoading && meds.length > 0 && !initialDataIds.current.meds) {
            initialDataIds.current.meds = new Set(meds.map(m => m.id));
        }
        if (!isLoadingSummary && extractedSummaryData?.length > 0 && !initialDataIds.current.summary) {
            initialDataIds.current.summary = new Set(extractedSummaryData.map(s => s.id));
        }
    }, [meds, extractedSummaryData, isLoading, isLoadingSummary]);

    const mutationConfig = (successMessage) => ({
        onSuccess: () => {
            if (successMessage) showSnackbar(successMessage, 'success');
            queryClient.invalidateQueries({ queryKey: medsQueryKey });
            setEditingMedId(null);
        },
        onError: (err) => showSnackbar(err.detail || 'An error occurred.', 'error'),
    });
    const addMutation = useMutation({ mutationFn: (payload) => authFetch(`directives/`, { method: 'POST', body: payload }), ...mutationConfig('Medication added successfully.') });
    const updateMutation = useMutation({ mutationFn: (vars) => authFetch(`directives/${vars.medId}`, { method: 'PATCH', body: vars.payload }), ...mutationConfig() });
    const uploadMutation = useMutation({ mutationFn: (vars) => authFetch(`episodes/${vars.episodeId}/initiate-med-rec`, { method: 'POST', body: vars.formData }), ...mutationConfig() });
    const aiReminderMutation = useMutation({ mutationFn: (vars) => authFetch(`directives/${vars.medId}/toggle-reminders`, { method: 'POST', body: { enabled: !!vars.enabled } }), ...mutationConfig('Reminder settings updated.') });
    const updateSummaryMutation = useMutation({
        mutationFn: (vars) => authFetch(`episodes/extracted-summary/${vars.dataId}`, { method: 'PATCH', body: vars.payload }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: summaryQueryKey });
            setEditingSummaryId(null);
            showSnackbar('Summary item updated.', 'success');
        },
        onError: (err) => showSnackbar(err.detail || 'An error occurred.', 'error'),
    });
    const deleteSummaryMutation = useMutation({
        mutationFn: (vars) => authFetch(`episodes/extracted-summary/${vars.dataId}`, { method: 'DELETE' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: summaryQueryKey });
            showSnackbar("Summary item deleted.", "success");
        },
        onError: (err) => showSnackbar(err.detail || 'Failed to delete item.', 'error'),
    });

    const saveProtocolMutation = useMutation({
        mutationFn: (payload) => authFetch(`protocols/`, { method: 'POST', body: payload }),
        onSuccess: () => {
            showSnackbar('Protocol saved successfully!', 'success');        
            queryClient.invalidateQueries({ queryKey: ['careProtocols', tenantId] });
            setShowSaveProtocolDialog(false);
        },
        onError: (err) => showSnackbar(err.detail || 'Failed to save protocol.', 'error'),
    });
    const deleteProtocolMutation = useMutation({
        mutationFn: (protocolId) => authFetch(`protocols/${protocolId}`, { method: 'DELETE' }),
        onSuccess: () => {
            showSnackbar('Protocol deleted successfully.', 'success');
            queryClient.invalidateQueries({ queryKey: ['careProtocols', tenantId] });
        },
        onError: (err) => showSnackbar(err.detail || 'Failed to delete protocol.', 'error'),
    });

    const handleAddMed = useCallback(() => {
        const episodeIdForPayload = episodeId || perpetualEpisodeId;
        if (!episodeIdForPayload) {
            showSnackbar("Cannot add medication: Patient's primary record not found.", "error");
            return;
        }

        const metadata = {
            ...newMedData.item_metadata,
            source: 'PROVIDER_ADDED'
        };

        const regimenItem = {
            item_name: newMedData.item_name,
            item_type: 'medication',
            frequency_type: newMedData.frequency_type,
            times_of_day: newMedData.frequency_type === 'tapering_schedule' ? [] : newMedData.times_of_day,
            item_metadata: metadata, // metadata no longer contains the schedule
            tapering_schedule: newMedData.frequency_type === 'tapering_schedule' ? newMedData.tapering_schedule : undefined, // Add schedule here
            regimen_start_date: new Date().toISOString().split('T')[0],
            reminders_active: true,
        };
        
        const isLongitudinal = newMedData.item_type === 'medication'; 
        const payload = {
            patient_user_id: patientUserId,
            episode_of_care_id: episodeIdForPayload,
            regimen_items: [regimenItem]
        };
        addMutation.mutate(payload, {
            onSuccess: () => {
                setNewMedData({
                    item_name: '',
                    frequency_type: 'daily',
                    times_of_day: ['09:00'],
                    item_metadata: { dosage: '', route: '', frequency_instruction: '' },
                    tapering_schedule: [{ start_day: 1, end_day: 7, frequency_type: 'daily', times_of_day: ['09:00'] }]
                });
                queryClient.invalidateQueries({ queryKey: medsQueryKey });
            }
        });
    }, [addMutation, newMedData, patientUserId, episodeId, perpetualEpisodeId, queryClient, medsQueryKey, showSnackbar]);

    const handleUpload = useCallback(() => {
        if (!dischargeSummaryFile || !targetEpisodeId) return; 
        const formData = new FormData();
        formData.append('summary_file', dischargeSummaryFile);
        
        setProcessingStatus({ progress: 0, message: 'Uploading document...' });
        setIsProcessingPdf(true); 

        uploadMutation.mutate({ episodeId: targetEpisodeId, formData }, { 
            onSuccess: () => {
                setDischargeSummaryFile(null);
                showSnackbar("Document uploaded. Analysis has started.", "info");
            },
            onError: (err) => {
                showSnackbar(err.detail || "Upload failed.", "error");
                setIsProcessingPdf(false);
                setProcessingStatus({ progress: 0, message: '' });
            }
        });
    }, [dischargeSummaryFile, targetEpisodeId, uploadMutation, showSnackbar]); 

    const sortedMeds = useMemo(() => {
        if (!Array.isArray(meds)) return [];
        const activeMeds = meds.filter(m => 
            (m.item_type === 'medication') && 
            m.status !== 'stopped' && 
            m.status !== 'rejected'
        );
        const getSortOrder = (status) => (status === 'pending_review' ? 0 : 1);
        return [...activeMeds].sort((a, b) => {
            const orderA = getSortOrder(a.status);
            const orderB = getSortOrder(b.status);
            if (orderA !== orderB) return orderA - orderB;
            return (a.item_name || '').localeCompare(b.item_name || '');
        });
    }, [meds]);

    
    const handleSaveEdit = useCallback((medToUpdate, newStatus = null) => {
        if (!medToUpdate) return;

        const metadata = {
            ...(medToUpdate.details.item_metadata || {}),
            dosage: editFormData.dosage,
            route: editFormData.route,
            frequency_instruction: editFormData.frequency_instruction,
        };
        
        delete metadata.tapering_schedule;

        const updatedDetails = {
            ...medToUpdate.details,
            item_name: editFormData.item_name,
            frequency_type: editFormData.frequency_type,
            times_of_day: editFormData.frequency_type === 'tapering_schedule' ? [] : editFormData.times_of_day,
            item_metadata: metadata,
            tapering_schedule: editFormData.frequency_type === 'tapering_schedule' ? editFormData.tapering_schedule : undefined,
        };

        const payload = {
            details: updatedDetails,
        };

        if (newStatus) {
            payload.status = newStatus;
            if (newStatus === 'active') {
                payload.details.reminders_active = true;
            }
        }

        updateMutation.mutate({ medId: medToUpdate.id, payload });
    }, [updateMutation, editFormData]);


    const handleUpdateStatus = useCallback((medToUpdate, status) => {
        if (medToUpdate.id === editingMedId) {
            handleSaveEdit(medToUpdate, status);
            return;
        }

        let payload = { status };
        if (status === 'active' && medToUpdate) {
            payload.details = { ...medToUpdate.details, reminders_active: true };
        }
        updateMutation.mutate({ medId: medToUpdate.id, payload });
    }, [updateMutation, editingMedId, handleSaveEdit]);

    const handleToggleReminder = useCallback((medId, enabled) => aiReminderMutation.mutate({ medId, enabled }), [aiReminderMutation]);
    
    const handleNewMedFrequencyChange = useCallback((e) => {
        const newFrequency = e.target.value;
        const defaultTimes = FREQUENCY_TO_TIMES_MAP[newFrequency] || [];
        setNewMedData(prev => ({
            ...prev,
            frequency_type: newFrequency,
            times_of_day: defaultTimes,
        }));
    }, []);

    const handleEditMedFrequencyChange = useCallback((e) => {
        const newFrequency = e.target.value;
        const defaultTimes = FREQUENCY_TO_TIMES_MAP[newFrequency] || [];
        setEditFormData(prev => ({
            ...prev,
            frequency_type: newFrequency,
            times_of_day: defaultTimes,
        }));
    }, []);

    const handleEditClick = useCallback((med) => {
        setEditingMedId(med.id);
        const details = med.details || {};
        const metadata = details.item_metadata || {};

        setEditFormData({
            item_name: details.item_name || '',
            dosage: metadata.dosage || '',
            route: metadata.route || '',
            frequency_instruction: metadata.frequency_instruction || '',
            frequency_type: details.frequency_type || 'once',
            times_of_day: details.times_of_day || [],
            
            // --- START OF SURGICAL FIX 1 ---
            // BUG: This was reading from `metadata.tapering_schedule`
            // FIX: Read from the correct location: `details.tapering_schedule`
            tapering_schedule: details.tapering_schedule || [{ 
            // --- END OF SURGICAL FIX 1 ---
                start_day: 1, 
                end_day: 7, 
                frequency_type: 'daily', 
                times_of_day: ['09:00'] 
            }],
        });
    }, []); 

    const handleOpenSaveDialog = () => {
        setProtocolName(`${newMedData.item_name} - Tapering Schedule`);
        setShowSaveProtocolDialog(true);
    };

    const handleSaveProtocol = () => {
        if (!protocolName.trim()) {
            showSnackbar('Protocol Name is required.', 'error');
            return;
        }
        const protocolDefinition = {
            medication_directives: [{
                item_name: newMedData.item_name,
                
                // --- START OF SURGICAL FIX 2 ---
                // BUG: This was missing, so Dosage/Route were not being saved.
                item_metadata: newMedData.item_metadata,
                // --- END OF SURGICAL FIX 2 ---

                tapering_schedule: newMedData.tapering_schedule,
            }]
        };

        saveProtocolMutation.mutate({

            name: protocolName,
            description: protocolDescription,
            protocol_type: 'medication_taper',
            module_definition: protocolDefinition,
            is_active: true
        });
    };    

    const handleApplyProtocol = (event) => {
        const protocolId = event.target.value;
        const selectedProtocol = careProtocols.find(p => p.id === protocolId);
        if (selectedProtocol) {
            const scheduleDef = selectedProtocol.module_definition?.medication_directives?.[0];
            if (scheduleDef) {
                setNewMedData({
                    item_name: scheduleDef.med_name || '',
                    frequency_type: 'tapering_schedule',
                    times_of_day: [],
                    item_metadata: scheduleDef.item_metadata || {},
                    
                    // --- START OF SURGICAL FIX 3 ---
                    // BUG: This was looking for 'scheduleDef.schedule'
                    // FIX: Look for the correct field 'scheduleDef.tapering_schedule'
                    tapering_schedule: scheduleDef.tapering_schedule || [],
                    // --- END OF SURGICAL FIX 3 ---

                });
                showSnackbar(`Applied protocol: ${selectedProtocol.name}`, 'success');
            }
        }
    };

    const handleCancelEdit = useCallback(() => setEditingMedId(null), []);
    
    const handleEditInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
    }, [setEditFormData]);

    const handleSummaryEditClick = useCallback((item) => {
        setEditingSummaryId(item.id);
        setEditSummaryData(item.data_content);
    }, []);

    const handleSummaryCancelEdit = useCallback(() => setEditingSummaryId(null), []);

    const handleSummaryInputChange = useCallback((key, value) => {
        setEditSummaryData(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleSummarySave = useCallback((dataId) => updateSummaryMutation.mutate({ dataId, payload: editSummaryData }), [updateSummaryMutation, editSummaryData]);

    const handleSummaryDelete = useCallback((dataId) => {
        if (window.confirm("Are you sure you want to delete this summary item?")) {
            deleteSummaryMutation.mutate({ dataId });
        }
    }, [deleteSummaryMutation]);

    const pendingReviewCount = useMemo(() => sortedMeds.filter(m => m.status === 'pending_review').length, [sortedMeds]);
    useEffect(() => {
        if (!isProcessingPdf || !targetEpisodeId) return;

        const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
        const apiHost = process.env.REACT_APP_API_HOST || 'localhost:8001';
        const wsUrl = `${wsScheme}://${apiHost}/api/episodes/ws/med-rec/${targetEpisodeId}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => logger.info(`WebSocket connection established for episode ${targetEpisodeId}.`);
        
        ws.onmessage = (event) => {
            logger.info("WebSocket message received:", event.data);
            try {
                const message = JSON.parse(event.data);
                if (message.status === 'in_progress') {
                    setProcessingStatus({ progress: message.progress, message: message.message });
                } else if (message.status === 'complete' || message.status === 'error') {
                    showSnackbar(message.message, message.status);
                    
                    queryClient.invalidateQueries({ queryKey: medsQueryKey });
                    queryClient.invalidateQueries({ queryKey: summaryQueryKey });

                    setJustCompleted(true);
                    setTimeout(() => setJustCompleted(false), 2000); 
                    
                    setIsProcessingPdf(false);
                }
            } catch (e) {
                logger.error("Failed to parse WebSocket message:", e);
                setIsProcessingPdf(false);
            }
        };

        ws.onerror = (event) => {
            logger.error("WebSocket connection error:", event);
            showSnackbar("Real-time update connection failed. Please refresh manually.", "error");
            setIsProcessingPdf(false);
        };

        ws.onclose = () => {
            logger.info(`WebSocket connection closed for episode ${targetEpisodeId}.`);
            setIsProcessingPdf(false);
            setProcessingStatus({ progress: 0, message: '' });
        };
        
        return () => ws.close();
    }, [isProcessingPdf, targetEpisodeId, queryClient, showSnackbar, medsQueryKey, summaryQueryKey]);
    
    return (
        <>
            <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="lg" PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column', [`@keyframes blink-green`]: { '0%': { backgroundColor: alpha('#66bb6a', 0) }, '50%': { backgroundColor: alpha('#66bb6a', 0.25) }, '100%': { backgroundColor: alpha('#66bb6a', 0) } } } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    {isEpisodeContext ? `Episode Clinical Summary Review for ${patientName}` : `Comprehensive Medication List for ${patientName}`}
                    <IconButton onClick={() => onClose(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: 'grey.50', display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', flexGrow: 1, gap: 3, overflow: 'hidden' }}>
                        {/* Left Column */}
                        <Box sx={{ width: '40%', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', pr: 1 }}>
                                                       
                            <Paper variant="outlined" sx={{ p: 2, flexShrink: 0 }}>
                                <Typography variant="h6" gutterBottom>Add New Medication</Typography>
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    size="small"
                                    options={sortedMedOptions}
                                    groupBy={(option) => option.type}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                    inputValue={newMedData.item_name}
                                    onInputChange={(event, newInputValue) => {
                                        setNewMedData(prev => ({ ...prev, item_name: newInputValue }));
                                    }}
                                    onChange={handleAutocompleteChange}
                                    renderInput={(params) => <TextField {...params} label="Search Protocols or Add New Medication" />}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            {option.label}
                                            {option.type === 'Protocol' && (
                                                <Tooltip title="Delete Protocol">
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        onClick={(event) => handleDeleteProtocol(option.data.id, event)}
                                                    >
                                                        <DeleteIcon fontSize="inherit" color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                    sx={{ mb: 1 }}
                                />                              
                                                               
                                <TextField fullWidth size="small" label="Dosage" value={newMedData.item_metadata.dosage} onChange={(e) => setNewMedData(p => ({ ...p, item_metadata: { ...p.item_metadata, dosage: e.target.value } }))} sx={{ mb: 1 }} />
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Frequency"
                                    value={newMedData.frequency_type}
                                    onChange={handleNewMedFrequencyChange}
                                    sx={{ mb: 1 }}
                                >
                                    {FREQUENCY_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {newMedData.frequency_type === 'tapering_schedule' ? (
                                    <Box sx={{mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1}}>
                                        <Typography variant="subtitle2" gutterBottom>Define Tapering Schedule</Typography>
                                        {newMedData.tapering_schedule.map((phase, index) => (
                                            <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                                                <Grid container spacing={1.5} alignItems="center">
                                                    <Grid item xs={12} sm={5} container spacing={1.5} alignItems="center">
                                                        <Grid item xs={5}><TextField fullWidth size="small" type="number" label={`Start Day`} value={phase.start_day} onChange={e => handleNewMedTaperPhaseChange(index, 'start_day', parseInt(e.target.value) || 1)} /></Grid>
                                                        <Grid item xs={2} sx={{textAlign: 'center'}}>-</Grid>
                                                        <Grid item xs={5}><TextField fullWidth size="small" type="number" label={`End Day`} value={phase.end_day} onChange={e => handleNewMedTaperPhaseChange(index, 'end_day', parseInt(e.target.value) || 1)} /></Grid>
                                                    </Grid>
                                                    <Grid item xs={12} sm={5}>
                                                        <TextField select fullWidth size="small" label="Frequency" value={phase.frequency_type} onChange={e => handleNewMedTaperPhaseChange(index, 'frequency_type', e.target.value)}>
                                                            {FREQUENCY_OPTIONS.filter(o => !['tapering_schedule', 'as_needed', 'once'].includes(o.value)).map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                                        </TextField>
                                                    </Grid>
                                                    <Grid item xs={12} sm={2} sx={{textAlign: 'right'}}>
                                                        <IconButton size="small" onClick={() => handleRemoveNewMedTaperPhase(index)} disabled={newMedData.tapering_schedule.length <= 1}>
                                                            <DeleteIcon fontSize="inherit"/>
                                                        </IconButton>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        {phase.times_of_day.map((time, timeIndex) => (
                                                            <TextField
                                                                key={timeIndex} size="small" label={`Time ${timeIndex + 1}`} type="time" value={time}
                                                                sx={{ width: '120px', mr: 1 }} InputLabelProps={{ shrink: true }}
                                                                onChange={e => {
                                                                    const newTimes = [...phase.times_of_day];
                                                                    newTimes[timeIndex] = e.target.value;
                                                                    handleNewMedTaperPhaseChange(index, 'times_of_day', newTimes);
                                                                }}
                                                            />
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        ))}
                                        <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAddNewMedTaperPhase}>Add Phase</Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ mb: 1 }}>
                                        {newMedData.times_of_day.map((time, index) => (
                                            <TextField
                                                key={index} size="small" label={`Time ${index + 1}`} type="time" value={time}
                                                onChange={(e) => {
                                                    const newTimes = [...newMedData.times_of_day];
                                                    newTimes[index] = e.target.value;
                                                    setNewMedData(p => ({ ...p, times_of_day: newTimes }));
                                                }}
                                                sx={{ width: '150px', mr: 1 }} InputLabelProps={{ shrink: true }}
                                            />
                                        ))}
                                    </Box>
                                )}

                                <TextField fullWidth size="small" label="Instruction Text" value={newMedData.item_metadata.frequency_instruction} onChange={(e) => setNewMedData(p => ({ ...p, item_metadata: { ...p.item_metadata, frequency_instruction: e.target.value } }))} sx={{ mb: 1, mt: 2 }} />
                                <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleAddMed} disabled={addMutation.isPending || !newMedData.item_name}>Add Manually</Button>
                                {newMedData.frequency_type === 'tapering_schedule' && (
                                    <Button
                                        variant="outlined"
                                        onClick={handleOpenSaveDialog}
                                        disabled={addMutation.isPending}
                                        sx={{ ml: 1 }}
                                    >
                                        Save Schedule as Reusable Protocol
                                    </Button>
                                )}
                            </Paper>
                            
                            <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.main' }}>
                                <Typography variant="h6" gutterBottom>AI-Assisted Reconciliation</Typography>
                                <Tooltip title={!targetEpisodeId ? "A patient context is required to use AI analysis." : "Upload a document for analysis"}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} disabled={!targetEpisodeId}>
                                            Upload File
                                            <input type="file" hidden accept=".pdf,image/jpeg,image/png" onChange={(e) => setDischargeSummaryFile(e.target.files[0])} disabled={!targetEpisodeId}/>
                                        </Button>
                                        <Button component="label" variant="outlined" startIcon={<CameraAltIcon />} disabled={!targetEpisodeId}>
                                            Use Camera
                                            <input type="file" hidden accept="image/*" capture="environment" onChange={(e) => setDischargeSummaryFile(e.target.files[0])} disabled={!targetEpisodeId}/>
                                        </Button>
                                    </Box>
                                </Tooltip>
                                {dischargeSummaryFile && <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Selected: {dischargeSummaryFile.name}</Typography>}
                                <Button onClick={handleUpload} disabled={!dischargeSummaryFile || uploadMutation.isPending || isProcessingPdf || !targetEpisodeId} sx={{ mt: 1 }}>Process with AI</Button>
                            </Paper>

                            <>
                                <Divider sx={{ pt: 1 }}><Chip label="Extracted Summary" size="small" /></Divider>
                                <ExtractedDataViewer
                                    data={extractedSummaryData} isLoading={isLoadingSummary} editingId={editingSummaryId}
                                    editData={editSummaryData} isSaving={updateSummaryMutation.isPending}
                                    isDeleting={deleteSummaryMutation.isPending} onEdit={handleSummaryEditClick}
                                    onCancel={handleSummaryCancelEdit} onInputChange={handleSummaryInputChange}
                                    onSave={handleSummarySave} onDelete={handleSummaryDelete}
                                    justCompleted={justCompleted} initialDataIds={initialDataIds}
                                />
                            
                                <Dialog open={showSaveProtocolDialog} onClose={() => setShowSaveProtocolDialog(false)}>
                                    <DialogTitle>Save as Reusable Protocol</DialogTitle>
                                    <DialogContent>
                                        <Typography variant="body2" sx={{mb: 2}}>
                                            Save this tapering schedule as a template to easily reuse for other patients.
                                        </Typography>
                                        <TextField
                                            autoFocus
                                            margin="dense"
                                            id="name"
                                            label="Protocol Name"
                                            type="text"
                                            fullWidth
                                            variant="outlined"
                                            value={protocolName}
                                            onChange={(e) => setProtocolName(e.target.value)}
                                        />
                                        <TextField
                                            margin="dense"
                                            id="description"
                                            label="Description (Optional)"
                                            type="text"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            variant="outlined"
                                            value={protocolDescription}
                                            onChange={(e) => setProtocolDescription(e.target.value)}
                                        />
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={() => setShowSaveProtocolDialog(false)}>Cancel</Button>
                                        <Button onClick={handleSaveProtocol} variant="contained" disabled={saveProtocolMutation.isPending}>
                                            {saveProtocolMutation.isPending ? <CircularProgress size={24} /> : "Save"}
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            
                            </>

                        </Box>
                        {/* Right Column */}
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexShrink: 0 }}>
                                <Typography variant="h6" component="div">Medications for Review</Typography>
                                {pendingReviewCount > 0 && <Chip icon={<PendingActionsIcon />} label={`${pendingReviewCount} Pending`} color="warning" size="small" />}
                            </Box>
                            
                            {isProcessingPdf && (
                                <Box sx={{ width: '100%', mb: 1.5 }}>
                                    <Typography variant="caption" sx={{ mb: 0.5, fontStyle: 'italic' }}>{processingStatus.message || 'Analyzing document...'}</Typography>
                                    <LinearProgress variant="determinate" value={processingStatus.progress || 0} />
                                </Box>
                            )}
                            
                            <Box sx={{ flex: 1, overflowY: 'auto', pr: 1.5, minHeight: 0 }}>
                                {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
                                {isError && <Alert severity="error">Error fetching medications: {error?.message}</Alert>}

                                {!isLoading && !isError && sortedMeds.map(med => (
                                    <MedicationRow
                                        key={med.id} 
                                        med={med}
                                        isUpdating={updateMutation.isPending || aiReminderMutation.isPending}
                                        isEditing={editingMedId === med.id} 
                                        editData={editFormData}
                                        onEdit={() => handleEditClick(med)}
                                        onSave={() => handleSaveEdit(med)}
                                        onCancel={handleCancelEdit} 
                                        onUpdateStatus={(status) => handleUpdateStatus(med, status)}
                                        onInputChange={handleEditInputChange}
                                        onFrequencyChange={handleEditMedFrequencyChange}
                                        onToggleReminder={(enabled) => handleToggleReminder(med.id, enabled)}
                                        justCompleted={justCompleted} 
                                        initialDataIds={initialDataIds}
                                        handleEditTaperPhaseChange={handleEditTaperPhaseChange}
                                        handleAddNewEditTaperPhase={handleAddNewEditTaperPhase}
                                        handleRemoveEditTaperPhase={handleRemoveEditTaperPhase}
                                    />
                                ))}

                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
                    <Button onClick={() => onClose(false)} color="primary" variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showSaveProtocolDialog} onClose={() => setShowSaveProtocolDialog(false)}>
                <DialogTitle>Save as Reusable Protocol</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{mb: 2}}>
                        Save this tapering schedule as a template to easily reuse for other patients.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Protocol Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={protocolName}
                        onChange={(e) => setProtocolName(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        id="description"
                        label="Description (Optional)"
                        type="text"
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        value={protocolDescription}
                        onChange={(e) => setProtocolDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSaveProtocolDialog(false)}>Cancel</Button>
                    <Button onClick={handleSaveProtocol} variant="contained" disabled={saveProtocolMutation.isPending}>
                        {saveProtocolMutation.isPending ? <CircularProgress size={24} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}